import type {Config} from '@/types'
import {ApiService, ResponseData} from './api'

export interface SpaceData
{
	id?: number;
	user_id?: number;
	title: string;
	description?: string | null;
}

export class MainService
{

	private readonly apiService: ApiService;
	private readonly cp_domain: string = '';
	private readonly main_path: string = '';

	constructor(apiService: ApiService)
	{
		if (!apiService)
			throw new Error('ApiService не инициализирован');
		if (!apiService.getConfig())
			throw new Error('Отсутствует конфигурация сервера');
		if (!apiService.getConfig().cp_domain)
			throw new Error('Отсутствует CP-домен в конфигурации сервера');
		this.apiService = apiService;
		this.cp_domain = apiService.getConfig().cp_domain;
		this.main_path = '/main/main/s';
	}

	/**
	 * Получить список всех пространств пользователя
	 */
	async getSpaces(): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/spaces',
				'GET'
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при получении списка пространств'
			};
		}
	}

	/**
	 * Определить стартовое пространство пользователя после входа в систему
	 */
	async resolveSpace(): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/resolve-space',
				'GET'
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при определении стартового пространства'
			};
		}
	}

	/**
	 * Создать новое пространство
	 */
	async createSpace(spaceData: SpaceData): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/space',
				'POST',
				spaceData
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при создании пространства'
			};
		}
	}

	/**
	 * Получить данные конкретного пространства
	 */
	async getSpace(spaceId: number): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/space/' + spaceId,
				'GET'
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при получении данных пространства'
			};
		}
	}

	/**
	 * Обновить пространство (частичное обновление)
	 */
	async updateSpace(spaceId: number, spaceData: Partial<SpaceData>): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/space/' + spaceId,
				'PATCH',
				spaceData
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при обновлении пространства'
			};
		}
	}

	/**
	 * Полностью обновить пространство (полная замена)
	 */
	async replaceSpace(spaceId: number, spaceData: SpaceData): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/space/' + spaceId,
				'PUT',
				spaceData
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при полной замене данных пространства'
			};
		}
	}

	/**
	 * Удалить пространство
	 */
	async deleteSpace(spaceId: number): Promise<ResponseData>
	{
		try {

			const response = await this.apiService.doFetch(
				this.cp_domain,
				this.main_path + '/space/' + spaceId,
				'DELETE'
			);

			return await this.apiService.getResponseJson(response);
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка при удалении пространства'
			};
		}
	}

}
